import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  LogOut, Users, Calendar, Clock, AlertCircle, Check, X, 
  RefreshCw, Plus, Edit, Trash2, Save, Trophy, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AnalyticsTab } from '../components/AnalyticsTab';
import { TennisCalendarTab } from '../components/admin/TennisCalendarTab';
import { MemberRowActions, ChangeMyPasswordButton } from '../components/admin/MemberActions';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'tennis');
  
  // Tennis state
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [tennisBookings, setTennisBookings] = useState([]);
  
  // Match state
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [matchFormData, setMatchFormData] = useState({
    team_id: '',
    opponent: '',
    date: '',
    time: '',
    location: '',
    is_home: true,
    competition: '',
    result: ''
  });

  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    let token = localStorage.getItem('admin_token');
    let adminData = localStorage.getItem('admin_user');

    // Samme konto brukes i tennis-innloggingen – gjenbruk den om admin-token mangler
    if ((!token || !adminData) && localStorage.getItem('tennis_token') && localStorage.getItem('tennis_member')) {
      const tennisMember = JSON.parse(localStorage.getItem('tennis_member'));
      if (tennisMember?.is_admin) {
        token = localStorage.getItem('tennis_token');
        adminData = localStorage.getItem('tennis_member');
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', adminData);
      }
    }
    
    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }

    const parsedAdmin = JSON.parse(adminData);
    
    if (!parsedAdmin.is_admin) {
      toast.error('Du har ikke admin-tilgang');
      navigate('/');
      return;
    }

    setAdmin(parsedAdmin);
    fetchAllData(token);
  }, [navigate]);

  const fetchAllData = async (token, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [pendingRes, membersRes, bookingsRes, teamsRes, matchesRes] = await Promise.all([
        fetch(`${API_URL}/api/tennis/admin/members/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tennis/admin/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tennis/admin/bookings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/matches/teams`),
        fetch(`${API_URL}/api/matches/all`)
      ]);

      if (pendingRes.ok) setPendingMembers(await pendingRes.json());
      if (membersRes.ok) setAllMembers(await membersRes.json());
      if (bookingsRes.ok) setTennisBookings(await bookingsRes.json());
      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (matchesRes.ok) setMatches(await matchesRes.json());
    } catch (error) {
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  // Tennis member actions
  const handleApproveMember = async (memberId) => {
    setActionLoading(memberId);
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/tennis/admin/members/${memberId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Medlem godkjent!');
        fetchAllData(token);
      } else {
        toast.error('Kunne ikke godkjenne medlem');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectMember = async (memberId) => {
    if (!window.confirm('Er du sikker på at du vil avvise denne brukeren?')) return;
    
    setActionLoading(memberId);
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/tennis/admin/members/${memberId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Medlem avvist');
        fetchAllData(token);
      } else {
        toast.error('Kunne ikke avvise medlem');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    } finally {
      setActionLoading(null);
    }
  };

  // Match actions
  const handleMatchSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    try {
      const url = editingMatch 
        ? `${API_URL}/api/matches/${editingMatch.id}`
        : `${API_URL}/api/matches`;
      
      const method = editingMatch ? 'PUT' : 'POST';
      
      const body = editingMatch 
        ? { 
            opponent: matchFormData.opponent,
            date: matchFormData.date,
            time: matchFormData.time,
            location: matchFormData.location,
            is_home: matchFormData.is_home,
            competition: matchFormData.competition,
            result: matchFormData.result || null
          }
        : matchFormData;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(editingMatch ? 'Kamp oppdatert!' : 'Kamp lagt til!');
        setShowMatchForm(false);
        setEditingMatch(null);
        resetMatchForm();
        fetchAllData(token);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Noe gikk galt');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    }
  };

  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setMatchFormData({
      team_id: match.team_id,
      opponent: match.opponent,
      date: match.date,
      time: match.time,
      location: match.location,
      is_home: match.is_home,
      competition: match.competition || '',
      result: match.result || ''
    });
    setShowMatchForm(true);
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne kampen?')) return;
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/matches/${matchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Kamp slettet');
        fetchAllData(token);
      } else {
        toast.error('Kunne ikke slette kamp');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    }
  };

  const resetMatchForm = () => {
    setMatchFormData({
      team_id: '',
      opponent: '',
      date: '',
      time: '',
      location: '',
      is_home: true,
      competition: '',
      result: ''
    });
  };

  const handleLogout = () => {
    ['admin_token', 'admin_user', 'tennis_token', 'tennis_member'].forEach((k) => localStorage.removeItem(k));
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-900">Laster...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-page" className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-500 hover:text-slate-900 transition-colors">
              <Home className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Admin Panel</h1>
              <p className="text-slate-500">Veitvet Sportsklubb - Administrasjon</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-sm hidden sm:block">
              Logget inn som: <span className="text-slate-900">{admin?.email}</span>
            </span>
            <ChangeMyPasswordButton />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAllData(localStorage.getItem('admin_token'))}
              className="border-white/20 text-slate-900 hover:bg-slate-100"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Oppdater
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/20 text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logg ut
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingMembers.length}</p>
                <p className="text-slate-500 text-sm">Venter godkjenning</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#CCFF00]/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#CCFF00]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{allMembers.filter(m => m.is_approved).length}</p>
                <p className="text-slate-500 text-sm">Tennis medlemmer</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{tennisBookings.filter(b => b.status === 'confirmed' && b.member_id !== 'admin-block').length}</p>
                <p className="text-slate-500 text-sm">Tennis bookinger</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{matches.length}</p>
                <p className="text-slate-500 text-sm">Fotballkamper</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-2 lg:flex gap-2 bg-transparent h-auto mb-6">
            <TabsTrigger 
              value="tennis"
              data-testid="admin-tab-tennis"
              className="lg:flex-1 data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 px-3 lg:px-6 py-3 lg:py-4 rounded-lg font-bold text-sm lg:text-base whitespace-nowrap transition-colors"
            >
              🎾 Tennis Booking
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              data-testid="admin-tab-calendar"
              className="lg:flex-1 data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 px-3 lg:px-6 py-3 lg:py-4 rounded-lg font-bold text-sm lg:text-base whitespace-nowrap transition-colors"
            >
              📅 Tenniskalender
            </TabsTrigger>
            <TabsTrigger 
              value="matches"
              data-testid="admin-tab-matches"
              className="lg:flex-1 data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 px-3 lg:px-6 py-3 lg:py-4 rounded-lg font-bold text-sm lg:text-base whitespace-nowrap transition-colors"
            >
              ⚽ Fotballkamper
            </TabsTrigger>
            <TabsTrigger 
              value="analytics"
              data-testid="admin-tab-analytics"
              className="lg:flex-1 data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 px-3 lg:px-6 py-3 lg:py-4 rounded-lg font-bold text-sm lg:text-base whitespace-nowrap transition-colors"
            >
              📊 Statistikk
            </TabsTrigger>
          </TabsList>

          {/* Tennis Tab */}
          <TabsContent value="tennis">
            <Tabs defaultValue="pending" className="w-full">
              <TabsList className="w-full flex justify-start gap-2 bg-transparent h-auto mb-6 overflow-x-auto">
                <TabsTrigger 
                  value="pending"
                  data-testid="admin-subtab-pending"
                  className="data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Venter godkjenning ({pendingMembers.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="members"
                  data-testid="admin-subtab-members"
                  className="data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Alle medlemmer
                </TabsTrigger>
                <TabsTrigger 
                  value="bookings"
                  data-testid="admin-subtab-bookings"
                  className="data-[state=active]:bg-[#22C55E] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:hover:bg-[#22C55E] bg-[#F1F5F9] text-[#1E293B] hover:bg-green-100 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Bookinger
                </TabsTrigger>
              </TabsList>

              {/* Pending Members */}
              <TabsContent value="pending">
                <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
                  {pendingMembers.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      Ingen medlemmer venter på godkjenning
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Navn</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">E-post</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Telefon</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Registrert</th>
                            <th className="text-right py-4 px-6 text-slate-500 font-medium">Handlinger</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingMembers.map((m) => (
                            <tr key={m.id} className="border-b border-white/5 hover:bg-slate-50">
                              <td className="py-4 px-6 text-slate-900 font-medium">{m.first_name} {m.last_name}</td>
                              <td className="py-4 px-6 text-slate-600">{m.email}</td>
                              <td className="py-4 px-6 text-slate-600">{m.phone}</td>
                              <td className="py-4 px-6 text-slate-500 text-sm">
                                {new Date(m.created_at).toLocaleDateString('no-NO')}
                              </td>
                              <td className="py-4 px-6 text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveMember(m.id)}
                                    disabled={actionLoading === m.id}
                                    className="bg-green-500 hover:bg-green-600 text-slate-900"
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Godkjenn
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectMember(m.id)}
                                    disabled={actionLoading === m.id}
                                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Avvis
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* All Members */}
              <TabsContent value="members">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                  <h3 className="text-lg font-bold text-slate-900">
                    Alle medlemmer ({allMembers.length})
                  </h3>
                  <Button
                    variant="outline"
                    data-testid="export-members-csv"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        const res = await fetch(
                          `${API_URL}/api/tennis/admin/members/export.csv`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `vsk-tennis-medlemmer-${new Date().toISOString().slice(0, 10)}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                        toast.success('CSV lastet ned');
                      } catch {
                        toast.error('Kunne ikke laste ned CSV');
                      }
                    }}
                  >
                    Last ned CSV
                  </Button>
                </div>
                <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-4 px-6 text-slate-500 font-medium">Navn</th>
                          <th className="text-left py-4 px-6 text-slate-500 font-medium">E-post</th>
                          <th className="text-left py-4 px-6 text-slate-500 font-medium">Telefon</th>
                          <th className="text-left py-4 px-6 text-slate-500 font-medium">Status</th>
                          <th className="text-right py-4 px-6 text-slate-500 font-medium">Handlinger</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allMembers.map((m) => (
                          <tr key={m.id} className="border-b border-white/5 hover:bg-slate-50">
                            <td className="py-4 px-6 text-slate-900 font-medium">
                              {m.first_name} {m.last_name}
                              {m.is_admin && (
                                <span className="ml-2 px-2 py-0.5 bg-[#CCFF00]/20 text-[#CCFF00] text-xs rounded">Admin</span>
                              )}
                            </td>
                            <td className="py-4 px-6 text-slate-600">{m.email}</td>
                            <td className="py-4 px-6 text-slate-600">{m.phone}</td>
                            <td className="py-4 px-6">
                              {m.is_approved ? (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Godkjent</span>
                              ) : (
                                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">Venter</span>
                              )}
                            </td>
                            <td className="py-4 px-6">
                              <MemberRowActions member={m} currentAdminId={admin?.id} onChanged={() => fetchAllData(localStorage.getItem('admin_token'), true)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Bookings */}
              <TabsContent value="bookings">
                <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
                  {tennisBookings.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      Ingen bookinger ennå
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Dato</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Tid</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Bane</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Medlem</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Telefon</th>
                            <th className="text-left py-4 px-6 text-slate-500 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tennisBookings
                            .filter((b) => b.member_id !== 'admin-block')
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((b) => (
                              <tr key={b.id} className="border-b border-white/5 hover:bg-slate-50">
                                <td className="py-4 px-6 text-slate-900 font-medium">
                                  {new Date(b.date).toLocaleDateString('no-NO')}
                                </td>
                                <td className="py-4 px-6 text-[#CCFF00] font-medium">{b.time_slot}</td>
                                <td className="py-4 px-6 text-slate-600">Bane {b.court_number}</td>
                                <td className="py-4 px-6 text-slate-600">{b.member_name}</td>
                                <td className="py-4 px-6 text-slate-600">{b.member_phone}</td>
                                <td className="py-4 px-6">
                                  {b.status === 'confirmed' ? (
                                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Bekreftet</span>
                                  ) : (
                                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Kansellert</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Tennis Calendar Tab */}
          <TabsContent value="calendar">
            <TennisCalendarTab members={allMembers} onChanged={() => fetchAllData(localStorage.getItem('admin_token'), true)} />
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">Administrer fotballkamper</h2>
              <Button
                onClick={() => {
                  setEditingMatch(null);
                  resetMatchForm();
                  setShowMatchForm(true);
                }}
                className="bg-[#CCFF00] text-black font-bold hover:bg-[#CCFF00]/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Legg til kamp
              </Button>
            </div>

            {/* Match Form Modal */}
            {showMatchForm && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl border border-white/10 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-slate-900">
                      {editingMatch ? 'Rediger kamp' : 'Legg til ny kamp'}
                    </h2>
                    <button 
                      onClick={() => {
                        setShowMatchForm(false);
                        setEditingMatch(null);
                        resetMatchForm();
                      }}
                      className="text-slate-500 hover:text-slate-900"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleMatchSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">Lag *</label>
                      <select
                        value={matchFormData.team_id}
                        onChange={(e) => setMatchFormData({ ...matchFormData, team_id: e.target.value })}
                        className="w-full bg-slate-100 border border-white/10 text-slate-900 rounded-md px-3 py-2"
                        required
                        disabled={!!editingMatch}
                      >
                        <option value="">Velg lag</option>
                        <optgroup label="Voksen">
                          {teams.filter(t => t.category === 'voksen').map(team => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Ungdom">
                          {teams.filter(t => t.category === 'ungdom').map(team => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">Motstander *</label>
                      <Input
                        type="text"
                        value={matchFormData.opponent}
                        onChange={(e) => setMatchFormData({ ...matchFormData, opponent: e.target.value })}
                        className="bg-slate-100 border-white/10 text-slate-900"
                        placeholder="F.eks. Grorud IL"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-2">Dato *</label>
                        <Input
                          type="date"
                          value={matchFormData.date}
                          onChange={(e) => setMatchFormData({ ...matchFormData, date: e.target.value })}
                          className="bg-slate-100 border-white/10 text-slate-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-2">Klokkeslett *</label>
                        <Input
                          type="time"
                          value={matchFormData.time}
                          onChange={(e) => setMatchFormData({ ...matchFormData, time: e.target.value })}
                          className="bg-slate-100 border-white/10 text-slate-900"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">Hjemme/Borte *</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="is_home"
                            checked={matchFormData.is_home}
                            onChange={() => setMatchFormData({ ...matchFormData, is_home: true })}
                            className="text-[#CCFF00]"
                          />
                          <span className="text-slate-900">Hjemmekamp</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="is_home"
                            checked={!matchFormData.is_home}
                            onChange={() => setMatchFormData({ ...matchFormData, is_home: false })}
                            className="text-[#CCFF00]"
                          />
                          <span className="text-slate-900">Bortekamp</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        {matchFormData.is_home ? 'Bane/Sted' : 'Bortebane/Adresse'}
                      </label>
                      <Input
                        type="text"
                        value={matchFormData.location}
                        onChange={(e) => setMatchFormData({ ...matchFormData, location: e.target.value })}
                        className="bg-slate-100 border-white/10 text-slate-900"
                        placeholder={matchFormData.is_home ? 'Veitvet kunstgress' : 'Motstanders bane'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-2">Turnering/Serie</label>
                      <Input
                        type="text"
                        value={matchFormData.competition}
                        onChange={(e) => setMatchFormData({ ...matchFormData, competition: e.target.value })}
                        className="bg-slate-100 border-white/10 text-slate-900"
                        placeholder="F.eks. 7. divisjon, Cup, Treningskamp"
                      />
                    </div>

                    {editingMatch && (
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-2">Resultat</label>
                        <Input
                          type="text"
                          value={matchFormData.result}
                          onChange={(e) => setMatchFormData({ ...matchFormData, result: e.target.value })}
                          className="bg-slate-100 border-white/10 text-slate-900"
                          placeholder="F.eks. 3-1"
                        />
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowMatchForm(false);
                          setEditingMatch(null);
                          resetMatchForm();
                        }}
                        className="flex-1 border-white/20 text-slate-900 hover:bg-slate-100"
                      >
                        Avbryt
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-[#CCFF00] text-black font-bold hover:bg-[#CCFF00]/90"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {editingMatch ? 'Lagre' : 'Legg til'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Matches Table */}
            <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
              {matches.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Ingen kamper registrert ennå. Klikk "Legg til kamp" for å starte.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Dato</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Lag</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Motstander</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">H/B</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Tid</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Serie</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Resultat</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Handlinger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((match) => (
                          <tr key={match.id} className="border-b border-white/5 hover:bg-slate-50">
                            <td className="py-3 px-4 text-slate-900">
                              {new Date(match.date).toLocaleDateString('no-NO', { 
                                day: 'numeric', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-600 rounded">
                                {match.team_name}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-900 font-medium">{match.opponent}</td>
                            <td className="py-3 px-4">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${
                                match.is_home 
                                  ? 'bg-[#CCFF00]/20 text-[#CCFF00]' 
                                  : 'bg-zinc-700 text-slate-600'
                              }`}>
                                {match.is_home ? 'H' : 'B'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{match.time}</td>
                            <td className="py-3 px-4 text-slate-500">{match.competition || '-'}</td>
                            <td className="py-3 px-4">
                              {match.result ? (
                                <span className="text-slate-900 font-bold">{match.result}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleEditMatch(match)}
                                  className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMatch(match.id)}
                                  className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Link to public calendar */}
            <div className="mt-6 text-center">
              <Link to="/kamper" className="text-[#CCFF00] hover:underline">
                Se offentlig kampkalender →
              </Link>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
